import { Button } from '@storeblocks/button';
import React from 'react';
import styled from 'styled-components';

import { Permission } from '@/api/permissions/models/Permission';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { Center } from '@/components/Center';
import { InternalLink } from '@/components/InternalLink';
import { useHasPermission } from '@/components/Permissions/useHasPermission';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { useGetTrackId } from '@/tracking/hooks/useGetTrackId';
import { TrackingElement } from '@/tracking/types/TrackingElement';

import { EllipsisWithHourglassIcon } from './EllipsisWithHourglassIcon';

/**
 * Displays a message indicating that the organization has no investments yet.
 */
export const NoInvestmentsMessage: React.FC = () => {
  const organizationCmId = useOrganizationCmId();
  const getTrackId = useGetTrackId();
  const texts = useTexts();
  const hasPermission = useHasPermission();

  return (
    <Center horizontal data-test="empty-dashboard-state">
      <WithGap gap="32">
        <EllipsisWithHourglassIcon />

        <WithGapCentered gap="8">
          <h2>{texts.emptyState.noInvestments.title}</h2>
          <p>{texts.emptyState.noInvestments.description}</p>
        </WithGapCentered>

        {hasPermission(Permission.OrdersWriteNotEligible) ? (
          <Center horizontal>
            <Button
              variant="primary"
              type="button"
              to={`/${organizationCmId}/trading/not-eligible`}
              as={InternalLink}
              data-trackid={getTrackId(
                'go-to-subscription-not-eligible',
                TrackingElement.Link,
              )}
              data-test="buy-funds-link-not-eligible"
            >
              {texts.emptyState.noInvestments.buyFund}
            </Button>
          </Center>
        ) : (
          hasPermission(Permission.OrdersRead) && (
            <WithGapCentered gap="8" direction="row">
              <Button
                variant="primary"
                type="button"
                to={`/${organizationCmId}/trading/subscription`}
                as={InternalLink}
                data-trackid={getTrackId(
                  'go-to-subscription',
                  TrackingElement.Link,
                )}
                data-test="buy-funds-link"
              >
                {texts.emptyState.noInvestments.buyFund}
              </Button>

              <Button
                variant="secondary"
                type="button"
                to={`/${organizationCmId}/trading/orders`}
                as={InternalLink}
                data-trackid={getTrackId(
                  'go-to-pending-orders',
                  TrackingElement.Link,
                )}
              >
                {texts.emptyState.noInvestments.orderOverview}
              </Button>
            </WithGapCentered>
          )
        )}
      </WithGap>
    </Center>
  );
};

const WithGapCentered = styled(WithGap)`
  align-items: center;
  justify-content: center;
`;
